if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        /*
          WINTR:
          We use our custom WINTR cart instead of Dawn's
          cart page / cart drawer.
        */
        this.cart = null;

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        /*
          Our WINTR cart is a drawer, so keep the accessibility
          hint that Add to Cart can open a dialog/drawer.
        */
        if (document.querySelector('[data-wintr-global-cart]')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        const spinner = this.querySelector('.loading__spinner');

        if (spinner) {
          spinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');

        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        /*
          IMPORTANT:
          Do NOT request Dawn cart sections here.

          Shopify simply adds the item through AJAX.
          Our WINTR drawer then fetches /cart.js itself.
        */
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;

        const linesUpdateDeferred =
          this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())

          .then((response) => {

            /*
              SHOPIFY ERROR
            */
            if (response.status) {

              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);

              this.dispatchCartErrorEvent(
                response.description || response.message,
                'INVALID'
              );

              linesUpdateDeferred?.reject(
                new Error(response.description || response.message)
              );

              const soldOutMessage =
                this.submitButton.querySelector('.sold-out-message');

              if (soldOutMessage) {
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }

              this.error = true;

              return;
            }


            /*
              SUCCESS
            */

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker =
              CartPerformance.createStartingMarker(
                'add:wait-for-subscribers'
              );

            if (!this.error) {

              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {

                CartPerformance.measureFromMarker(
                  'add:wait-for-subscribers',
                  startMarker
                );

              });

            }

            this.error = false;


            /*
              If this Add to Cart happened inside a Quick Add modal,
              close the modal first.
            */

            const quickAddModal =
              this.closest('quick-add-modal');

            if (quickAddModal) {

              document.body.addEventListener(
                'modalClosed',
                () => {

                  setTimeout(() => {

                    if (typeof window.openWintrCart === 'function') {
                      window.openWintrCart();
                    }

                  }, 100);

                },
                { once: true }
              );

              quickAddModal.hide(true);

            } else {

              /*
                THIS IS THE IMPORTANT PART:

                Product successfully added
                         ↓
                refresh WINTR cart
                         ↓
                slide WINTR cart open

                NO PAGE RELOAD.
              */

              if (typeof window.openWintrCart === 'function') {

                window.openWintrCart();

              } else {

                /*
                  Safety fallback:
                  Product is still added even if the custom
                  WINTR drawer script ever fails.
                */

                console.warn(
                  'WINTR cart drawer was not available.'
                );

              }

            }

          })

          .catch((e) => {

            console.error(e);

            this.dispatchCartErrorEvent(
              e.message || 'Network error',
              'SERVICE_UNAVAILABLE'
            );

            linesUpdateDeferred?.reject(e);

          })

          .finally(() => {

            this.submitButton.classList.remove('loading');

            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }

            const spinner = this.querySelector('.loading__spinner');

            if (spinner) {
              spinner.classList.add('hidden');
            }

            CartPerformance.measureFromEvent(
              'add:user-action',
              evt
            );

          });
      }


      handleErrorMessage(errorMessage = false) {

        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }

      }


      toggleSubmitButton(disable = true, text) {

        if (disable) {

          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          if (text) {
            this.submitButtonText.textContent = text;
          }

        } else {

          this.submitButton.removeAttribute('disabled');

          this.submitButtonText.textContent =
            window.variantStrings.addToCart;

        }

      }


      createCartLinesUpdateEvent(variantId, quantity) {

        const { CartLinesUpdateEvent } =
          window.StandardEvents || {};

        if (!CartLinesUpdateEvent) return null;

        const deferred =
          CartLinesUpdateEvent.createPromise();

        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [
              {
                merchandiseId: variantId,
                quantity
              }
            ],
            promise: deferred.promise,
          })
        );

        return deferred;

      }


      resolveCartLinesUpdate(deferred) {

        if (!deferred) return;

        const { CartLinesUpdateEvent } =
          window.StandardEvents || {};

        if (!CartLinesUpdateEvent) return;


        const pendingCartDataPromise =
          typeof CartItems !== 'undefined'

            ? CartItems.fetchCartData()

            : fetch(`${routes.cart_url}.json`)
                .then((response) => response.json());


        pendingCartDataPromise

          .then((cart) => {

            if (!cart?.currency) {

              return deferred.reject(
                new Error(
                  'Missing currency in cart response'
                )
              );

            }

            deferred.resolve({
              cart:
                CartLinesUpdateEvent.createCartFromAjaxResponse(
                  cart
                )
            });

          })

          .catch((e) => deferred.reject(e));

      }


      dispatchCartErrorEvent(message, code) {

        const { CartErrorEvent } =
          window.StandardEvents || {};

        if (!CartErrorEvent) return;

        this.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code
          })
        );

      }


      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

    }
  );
}